import React, { useEffect, useState } from 'react'
import { getApi } from '~/util/api'
import ProgressContainer from '../ProgressContainer'
import { ActionPlanProvider, useActionPlan } from '../ActionPlanProvider'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import { Provider } from 'react-redux'
import { store } from '~/lib/store'
import { setProgressPercent } from '../ActionPlanSlice'
import { getUserTrackerSections } from '../index'
import { itemTypeIconMap } from '../TrackTileComponents/ActiveTile'
import { TimeIcon } from '../TrackTileComponents/icons'
import { AddedIcon } from '~/components/CoachCandidateView/ActionPlan/icons'
import HeadshotCircle from '~/components/coach_services/MeetYourCoach/HeadshotCircle'
import { useMediaQueryScreen } from '~/hooks/useMediaQuery'
import { normalizeLocale } from '~/util/normalizeLocale'
import translations from '../locales.json'
import {
  ArrowUturnLeftIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'

const CompletedJourneyInner = () => {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [weekFilter, setWeekFilter] = useState('all')
  const isLgScreen = useMediaQueryScreen('lg')
  const actionPlanProgressClass = isLgScreen ? 'mb-[6px]' : 'hidden'

  useEffect(() => {
    getUserTrackerSections()
    ;(async () => {
      try {
        const res = await getApi().get('/tracker/completed_resources')
        if (res.status === 200) {
          store.dispatch(setProgressPercent(res.data.progress))
          setData(res.data)
        }
      } catch {
        // ignore fetch errors
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const completed = React.useMemo(() => data?.completed || [], [data])
  const resourcesCompleted = data?.resourcesCompleted || 0
  const totalMinutes = data?.totalMinutes || 0
  const streak = data?.streak || 0

  const learningHoursLabel = React.useMemo(() => {
    if (totalMinutes === 0) return '—'
    if (totalMinutes < 60)
      return t('CompletedJourney.duration_minutes', { minutes: totalMinutes })
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return m > 0
      ? t('CompletedJourney.duration_hours_minutes', { hours: h, minutes: m })
      : t('CompletedJourney.duration_hours', { hours: h })
  }, [totalMinutes, t])

  const weekOptions = React.useMemo(() => {
    const seen = new Set()
    const opts = []
    completed.forEach((r) => {
      if (r.sectionKey && !seen.has(r.sectionKey)) {
        seen.add(r.sectionKey)
        const [period, ...rest] = r.sectionKey.split('_')
        const number = rest.join('-')
        const indicatorKey = period ? `${period}_indicator` : null
        const label =
          indicatorKey && number ? t(indicatorKey, { number }) : r.sectionKey
        opts.push({ key: r.sectionKey, label })
      }
    })
    const periodOrder = { day: 0, week: 1, month: 2, months: 3 }
    return opts.sort((a, b) => {
      const [aPeriod, aNum] = a.key.split('_')
      const [bPeriod, bNum] = b.key.split('_')
      const periodDiff =
        (periodOrder[aPeriod] ?? 99) - (periodOrder[bPeriod] ?? 99)
      return periodDiff !== 0 ? periodDiff : parseInt(aNum) - parseInt(bNum)
    })
  }, [completed, t])

  const filtered =
    weekFilter === 'all'
      ? completed
      : completed.filter((r) => r.sectionKey === weekFilter)

  if (loading) {
    return (
      <div className="m-4 md:m-10">
        <div className="mb-6 flex flex-col justify-between gap-6 lg:flex-row">
          <div className="flex flex-col gap-1">
            <h1 className="m-0 p-0 font-roboto-slab text-[28px] font-[700] leading-tight text-primary">
              {t('CompletedJourney.completed_journey_title')}
            </h1>
            <div className="mt-2 h-4 w-72 animate-pulse rounded bg-gray-200" />
            <div className="mt-1 h-3 w-40 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="flex flex-col gap-2 font-roboto-slab md:min-w-max lg:items-center">
            <h2 className={actionPlanProgressClass}>
              {t('action_plan_progress')}
            </h2>
            <ProgressContainer />
          </div>
        </div>
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-5 rounded-xl border border-gray-300 bg-white px-6 py-8"
            >
              <div className="h-16 w-16 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
              <div className="flex flex-col gap-2">
                <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-6 w-10 animate-pulse rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-stretch overflow-hidden rounded-xl border border-gray-300 bg-white"
            >
              <div className="w-[5px] flex-shrink-0 self-stretch bg-gray-100" />
              <div
                className="flex flex-shrink-0 items-center py-3 pl-4 pr-3"
                style={{ width: 176 }}
              >
                <div className="h-[100px] w-full animate-pulse rounded-md bg-gray-200" />
              </div>
              <div className="flex flex-1 flex-col justify-center gap-2 py-4 pl-2 pr-4">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                  <div className="h-5 w-28 animate-pulse rounded-full bg-gray-200" />
                </div>
                <div className="h-3 w-72 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="flex flex-shrink-0 items-center px-5">
                <div className="h-8 w-28 animate-pulse rounded-full bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="m-4 md:m-10">
      <div className="mb-6 flex flex-col justify-between gap-6 lg:flex-row">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 p-0 font-roboto-slab text-[28px] font-[700] leading-tight text-primary">
            {t('CompletedJourney.completed_journey_title')}
          </h1>
          <p className="m-0 mt-3 text-[15px] text-gray-600">
            {t('CompletedJourney.completed_journey_subtitle')}
          </p>
          <a
            href="/orientation/action_plan"
            className="inline-flex items-center gap-1.5 text-base text-link text-primary underline"
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
            {t('CompletedJourney.return_to_action_plan')}
          </a>
        </div>
        <div className="flex flex-col gap-2 font-roboto-slab md:min-w-max lg:items-center">
          <h2 className={actionPlanProgressClass}>
            {t('action_plan_progress')}
          </h2>
          <ProgressContainer />
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={<CheckBadgeIcon height={20} />}
          iconBg="bg-willow-brook-100"
          iconColor="text-willow-brook-600"
          label={t('CompletedJourney.resources_completed')}
          value={resourcesCompleted > 0 ? String(resourcesCompleted) : '—'}
        />
        <SummaryCard
          icon={<ClockIcon height={20} />}
          iconBg="bg-intoo-blue-bright-100"
          iconColor="text-intoo-blue-medium"
          label={t('CompletedJourney.learning_hours')}
          value={learningHoursLabel}
        />
        <SummaryCard
          icon={<TrophyIcon height={20} />}
          iconBg="bg-beeswax-100"
          iconColor="text-amber-600"
          label={t('CompletedJourney.current_streak')}
          value={
            streak > 0
              ? t(
                  streak === 1
                    ? 'CompletedJourney.streak_days'
                    : 'CompletedJourney.streak_days_plural',
                  { count: streak },
                )
              : '—'
          }
        />
      </div>

      {weekOptions.length > 0 && (
        <div className="mb-6">
          <select
            className="min-w-[160px] rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-10 text-sm text-gray-700 focus:outline-none"
            value={weekFilter}
            onChange={(e) => setWeekFilter(e.target.value)}
          >
            <option value="all">{t('CompletedJourney.all_weeks')}</option>
            {weekOptions.map(({ key, label }) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-[16px] border border-gray-300 bg-white p-8 text-center">
          <p className="mb-1 font-semibold text-gray-700">
            {t('CompletedJourney.no_completed_tasks')}
          </p>
          <p className="text-sm text-gray-600">
            {t('CompletedJourney.no_completed_tasks_cta')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((resource, i) => (
            <CompletedResourceCard
              key={resource.slug || i}
              resource={resource}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const SummaryCard = ({ icon, iconBg, iconColor, label, value }) => (
  <div className="flex items-center gap-6 rounded-xl border border-gray-300 bg-white px-8 py-8 shadow">
    <div
      className={`flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full ${iconBg} ${iconColor}`}
    >
      {React.cloneElement(icon, { height: 40, width: 40 })}
    </div>
    <div className="flex flex-col gap-3">
      <div className="text-sm text-gray-900">{label}</div>
      <div className="text-2xl font-bold leading-none text-gray-800">
        {value}
      </div>
    </div>
  </div>
)

const CompletedResourceCard = ({ resource }) => {
  const { t, locale } = useTranslation()
  const { setConversationStarter } = useActionPlan()
  const isCoachAdded = resource.coach_added
  const isCoachService = resource.coach_service
  const isCoachResource =
    resource.type === 'coach_resource' || resource.type === 'coach_service'
  const railColor =
    isCoachAdded || isCoachService ? 'bg-intoo-green-coachcta' : 'bg-primary'
  const typeIcon = resource.type ? itemTypeIconMap[resource.type] : null
  const ctaLabel = resource.type
    ? t(`CompletedJourney.cta_labels.${resource.type}`, {
        defaultValue: t('CompletedJourney.revisit'),
      })
    : t('CompletedJourney.revisit')
  const metaColor = isCoachService
    ? 'text-intoo-green-coachcta'
    : 'text-primary'

  const dateLocale = normalizeLocale(locale)
  const completedDate = resource.completedAt
    ? new Date(resource.completedAt).toLocaleDateString(
        dateLocale.endsWith('-US') ? 'en-US' : dateLocale,
        {
          month: 'numeric',
          day: 'numeric',
        },
      )
    : null

  return (
    <div className="relative flex flex-col gap-6 rounded-[16px] border border-gray-300 bg-white p-[16px] pl-[24px] lg:grid lg:grid-cols-12 lg:gap-0 lg:pl-[16px]">
      <div
        className={`absolute left-0 top-0 z-50 h-full w-[8px] rounded-bl-[16px] rounded-tl-[16px] ${railColor}`}
      />

      <div className="lg:col-span-3">
        <div className="flex h-full flex-col items-center justify-center">
          {isCoachResource ? (
            <HeadshotCircle
              coachImage={resource.image?.path}
              isCoachResource={true}
            />
          ) : (
            <div
              className="h-[120px] w-full overflow-hidden md:w-[80%]"
              style={{
                backgroundImage: resource.image?.path
                  ? `url(${resource.image.path})`
                  : '',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: resource.image?.path ? '' : '#eff6ff',
              }}
            />
          )}
        </div>
      </div>

      <div className="col-span-6 flex flex-col gap-2 lg:pr-6 xl:col-span-7">
        <div className="flex flex-wrap items-center gap-2">
          <span className="subtitle !text-xl">
            {resource.name || resource.slug}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-willow-brook-100 px-3 py-1 text-sm font-semibold text-willow-brook-500">
            <CheckCircleIcon className="h-4 w-4" />
            {completedDate
              ? t('CompletedJourney.completed_date', { date: completedDate })
              : t('CompletedJourney.completed_label')}
          </span>
        </div>

        {resource.description && (
          <p className="m-0 text-sm text-gray-600">{resource.description}</p>
        )}

        <div className="flex md:flex-row">
          {resource.length && (
            <div className="flex flex-col items-center justify-center gap-2 border-r border-cod-gray-200 pr-6">
              <TimeIcon variant={isCoachService ? 'coach' : 'primary'} />
              <div className={`capitalize ${metaColor}`}>{resource.length}</div>
            </div>
          )}
          {typeIcon && (
            <div
              className={`flex flex-col items-center justify-center gap-2 ${
                isCoachAdded ? 'px-3' : 'pl-6'
              }`}
            >
              {React.cloneElement(typeIcon, {
                className: metaColor,
                height: '24',
              })}
              <div className={`capitalize ${metaColor}`}>
                {t(`CompletedJourney.type_labels.${resource.type}`)}
              </div>
            </div>
          )}
          {isCoachAdded && (
            <div className="flex flex-col items-center justify-center gap-2 border-l border-cod-gray-200 pl-6">
              <AddedIcon className="text-intoo-green-coachcta" height="24" />
              <div className="capitalize text-intoo-green-coachcta">
                {t('CompletedJourney.coach_added_label')}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="col-span-3 flex h-full flex-col items-center justify-center xl:col-span-2">
        {(resource.url || resource.conversation_starter) && (
          <a
            href={resource.url || 'javascript:;'}
            target={resource.url ? '_blank' : undefined}
            rel={resource.url ? 'noreferrer' : undefined}
            data-toggle={resource.conversation_starter ? 'modal' : undefined}
            data-target={
              resource.conversation_starter ? '#coach_chat_list' : undefined
            }
            onClick={() =>
              resource.conversation_starter &&
              setConversationStarter(resource.conversation_starter)
            }
            className={`inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-[13px] font-semibold no-underline transition-colors ${
              resource.conversation_starter ? 'open-chat-modal ' : ''
            }${
              isCoachService
                ? 'border-intoo-green-coachcta text-intoo-green-coachcta hover:bg-intoo-green-coachcta hover:text-white'
                : 'border-primary text-primary hover:bg-primary hover:text-white'
            }`}
          >
            {ctaLabel}
            {typeIcon && (
              <span className="ml-1" aria-hidden="true">
                {React.cloneElement(typeIcon, { height: '16' })}
              </span>
            )}
          </a>
        )}
      </div>
    </div>
  )
}

const CompletedJourney = ({ s3_base_url, platformName, locale }) => (
  <Provider store={store}>
    <ActionPlanProvider s3_base_url={s3_base_url} platformName={platformName}>
      <TranslationProvider
        locale={locale}
        translations={translations}
        scope="javascript.components.ActionPlan"
      >
        <CompletedJourneyInner />
      </TranslationProvider>
    </ActionPlanProvider>
  </Provider>
)

export default CompletedJourney
